import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { BiMailSend } from "react-icons/bi";
import { FaInstagram, FaTiktok } from "react-icons/fa";

import MainContainer from "../components/MainContainer";
import Reveal from "../components/utils/Reveal";
import messagesService from "../api/services/messages";

const REASONS = [
  {
    kanji: "質",
    title: "Kvalitet",
    text: "Visokokvalitetni materijali koji dišu i prate svaki pokret.",
  },
  {
    kanji: "型",
    title: "Dizajn",
    text: "Moderan, sportski dizajn koji se ističe — u teretani i van nje.",
  },
  {
    kanji: "筋",
    title: "Krojevi",
    text: "Svaki kroj je pažljivo testiran u treningu, ne za katalog.",
  },
  {
    kanji: "歩",
    title: "Podrška",
    text: "Brza dostava i podrška na svakom koraku — tu smo za tebe.",
  },
];

const AboutPage = () => {
  const navigate = useNavigate();

  const [msgForm, setMsgForm] = useState({ email: "", subject: "", message: "" });
  const [msgStatus, setMsgStatus] = useState(null); // null | "sending" | "sent" | "error"
  const [msgError, setMsgError] = useState("");

  const setMsgField = (name) => (e) => {
    setMsgForm((f) => ({ ...f, [name]: e.target.value }));
    setMsgStatus(null);
  };

  const handleSendMessage = () => {
    if (msgStatus === "sending") return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(msgForm.email.trim())) {
      setMsgStatus("error");
      setMsgError("Unesi ispravnu e-mail adresu.");
      return;
    }
    if (!msgForm.subject.trim() || !msgForm.message.trim()) {
      setMsgStatus("error");
      setMsgError("Naslov i poruka su obavezni.");
      return;
    }
    setMsgStatus("sending");
    messagesService
      .createMessage({
        email: msgForm.email.trim(),
        subject: msgForm.subject.trim(),
        message: msgForm.message.trim(),
      })
      .then(() => {
        setMsgStatus("sent");
        setMsgForm({ email: "", subject: "", message: "" });
      })
      .catch((err) => {
        setMsgStatus("error");
        setMsgError(err.message || "Greška pri slanju poruke.");
      });
  };

  return (
    <MainContainer offCenter hideLogo>
      <Head>
        <Kicker>改善 Obsessed — Novi Sad</Kicker>
        <Title>
          Brend rođen iz <i>opsesije</i>
        </Title>
        <Intro>
          <strong>Obsessed</strong> je rođen iz ljubavi prema treningu, snazi i
          stilu. Vrhunski gym wear — uske i oversized majice, stringeri, šorcevi
          i čarape koji kombinuju udobnost, kvalitet i moderan izgled.
        </Intro>
      </Head>

      <Wrap>
        <Reveal>
          <Mission>
            <span className="kj">改善</span>
            <div>
              <h3>Naša misija</h3>
              <p>
                Da ti obezbedimo opremu koja te pokreće, motiviše i čini da se
                osećaš moćno — u teretani, na ulici ili kod kuće. Svaki komad je
                dizajniran da izdrži intenzivne treninge i istakne tvoju formu.
                Kaizen — svaki dan 1% bolje.
              </p>
            </div>
          </Mission>
        </Reveal>

        <Reveal>
          <SectionLabel>Zašto Obsessed?</SectionLabel>
        </Reveal>

        <Grid>
          {REASONS.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.06}>
              <Card>
                <span className="kj">{r.kanji}</span>
                <b>{r.title}</b>
                <p>{r.text}</p>
              </Card>
            </Reveal>
          ))}
        </Grid>

        <Reveal>
          <ContactCard>
            <div>
              <h3>Pitanja? Saradnja? Direktna porudžbina?</h3>
              <p>Tu smo za tebe — javi se.</p>
            </div>
            <Links>
              <a href="mailto:info@obsessed.rs">
                <BiMailSend size={16} /> info@obsessed.rs
              </a>
              <a
                href="https://instagram.com/obsessed.rs"
                target="_blank"
                rel="noreferrer"
              >
                <FaInstagram size={16} /> @obsessed.rs
              </a>
              <a
                href="https://www.tiktok.com/@obsessed.rs"
                target="_blank"
                rel="noreferrer"
              >
                <FaTiktok size={14} /> @obsessed.rs
              </a>
            </Links>
          </ContactCard>
        </Reveal>

        <Reveal>
          <FormCard>
            <h3>Pošalji nam poruku</h3>
            <FormRow>
              <FormField>
                <label>E-mail</label>
                <input
                  type="email"
                  placeholder="tvoj@email.com"
                  value={msgForm.email}
                  onChange={setMsgField("email")}
                />
              </FormField>
              <FormField>
                <label>Naslov</label>
                <input
                  placeholder="npr. Pitanje za veličinu"
                  value={msgForm.subject}
                  onChange={setMsgField("subject")}
                />
              </FormField>
            </FormRow>
            <FormField>
              <label>Poruka</label>
              <textarea
                placeholder="Tvoja poruka…"
                value={msgForm.message}
                onChange={setMsgField("message")}
              />
            </FormField>

            {msgStatus === "error" && <FormError>{msgError}</FormError>}
            {msgStatus === "sent" && (
              <FormOk>✓ Poruka je poslata — javićemo se uskoro!</FormOk>
            )}

            <SendBtn
              $disabled={msgStatus === "sending"}
              onClick={handleSendMessage}
            >
              {msgStatus === "sending" ? "Slanje…" : "Pošalji poruku"}
              {msgStatus !== "sending" && <i>→</i>}
            </SendBtn>
          </FormCard>
        </Reveal>

        <Reveal>
          <CtaRow>
            <BtnRed
              onClick={() => {
                navigate("/prodavnica");
                window.scrollTo({ top: 0, behavior: "instant" });
              }}
            >
              Pogledaj kolekciju <i>→</i>
            </BtnRed>
          </CtaRow>
        </Reveal>
      </Wrap>
    </MainContainer>
  );
};

export default AboutPage;

const Head = styled.div`
  width: 100%;
  max-width: 116rem;
  margin: 0 auto;
  padding: 2rem 2rem 3.4rem;
`;

const Kicker = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  font-size: 1.1rem;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--red, #fe5f55);
  font-family: "Poppins-Medium";
  margin-bottom: 1.2rem;

  &::before {
    content: "";
    width: 3.4rem;
    height: 1px;
    background: var(--red, #fe5f55);
  }
`;

const Title = styled.h1`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  font-size: clamp(3.2rem, 5.4vw, 5.6rem);
  line-height: 1;
  letter-spacing: 0.01em;
  color: var(--text, #f4f2ee);
  margin-bottom: 1.8rem;

  i {
    font-style: normal;
    color: var(--red, #fe5f55);
  }
`;

const Intro = styled.p`
  font-size: 1.6rem;
  font-family: "Poppins-Regular";
  color: var(--muted, #8e8c94);
  line-height: 1.8;
  max-width: 64rem;

  strong {
    font-family: "Poppins-SemiBold";
    color: var(--text, #f4f2ee);
  }
`;

const Wrap = styled.div`
  width: 100%;
  max-width: 116rem;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const Mission = styled.div`
  display: flex;
  align-items: center;
  gap: 2.6rem;
  background: var(--bg2, #17171c);
  border: 1px solid rgba(254, 95, 85, 0.22);
  border-radius: 0.6rem;
  padding: 2.8rem;
  margin-bottom: 3.4rem;

  .kj {
    font-family: var(--font-kanji);
    font-size: 6.4rem;
    line-height: 1;
    color: rgba(254, 95, 85, 0.45);
    flex-shrink: 0;
  }

  h3 {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 2rem;
    color: var(--text, #f4f2ee);
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.4rem;
    font-family: "Poppins-Regular";
    color: var(--muted, #8e8c94);
    line-height: 1.7;
    max-width: 72rem;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SectionLabel = styled.div`
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-size: 2.4rem;
  color: var(--text, #f4f2ee);
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1.4rem;

  &::before {
    content: "";
    width: 3.4rem;
    height: 1px;
    background: var(--red, #fe5f55);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.6rem;
  margin-bottom: 3.4rem;

  & > div {
    height: 100%;
  }

  @media (max-width: 1030px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--bg1, #101013);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.6rem;
  padding: 2.4rem;
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s var(--ease, ease);

  &:hover {
    border-color: rgba(254, 95, 85, 0.35);
  }

  .kj {
    display: block;
    font-family: var(--font-kanji);
    font-size: 3.2rem;
    line-height: 1;
    color: var(--red, #fe5f55);
    margin-bottom: 1.6rem;
  }

  b {
    display: block;
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 1.7rem;
    color: var(--text, #f4f2ee);
    margin-bottom: 0.8rem;
  }

  p {
    font-size: 1.3rem;
    font-family: "Poppins-Regular";
    color: var(--muted, #8e8c94);
    line-height: 1.7;
  }
`;

const ContactCard = styled.div`
  background: var(--bg2, #17171c);
  border: 1px solid rgba(254, 95, 85, 0.22);
  border-radius: 0.6rem;
  padding: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 3.4rem;

  h3 {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 1.8rem;
    color: var(--text, #f4f2ee);
    margin-bottom: 0.6rem;
  }

  p {
    font-size: 1.3rem;
    font-family: "Poppins-Regular";
    color: var(--muted, #8e8c94);
  }
`;

const Links = styled.div`
  display: flex;
  gap: 1.2rem;
  flex-wrap: wrap;

  a {
    display: inline-flex;
    align-items: center;
    gap: 0.8rem;
    font-size: 1.2rem;
    letter-spacing: 0.06em;
    font-family: "Poppins-Medium";
    color: var(--red, #fe5f55);
    border: 1px solid rgba(254, 95, 85, 0.4);
    border-radius: 0.4rem;
    padding: 1rem 1.6rem;
    text-decoration: none;
    transition: all 0.25s var(--ease, ease);

    &:hover {
      background: var(--red, #fe5f55);
      color: var(--bg0, #0a0a0c);
    }
  }
`;

const FormCard = styled.div`
  background: var(--bg1, #101013);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  border-radius: 0.6rem;
  padding: 2.8rem;
  margin-bottom: 3.4rem;

  h3 {
    font-family: "Anton", sans-serif;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 2rem;
    color: var(--text, #f4f2ee);
    margin-bottom: 2rem;
    display: flex;
    align-items: center;
    gap: 1.4rem;

    &::before {
      content: "";
      width: 3.4rem;
      height: 1px;
      background: var(--red, #fe5f55);
    }
  }
`;

const FormRow = styled.div`
  display: flex;
  gap: 1.4rem;

  & > div {
    flex: 1;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 0;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  margin-bottom: 1.6rem;

  label {
    font-size: 1.1rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-family: "Poppins-Medium";
    color: var(--muted, #8e8c94);
  }

  input,
  textarea {
    width: 100%;
    padding: 1.2rem 1.4rem;
    font-size: 1.4rem;
    font-family: "Poppins-Regular";
    background: var(--bg0, #0a0a0c);
    color: var(--text, #f4f2ee);
    border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
    border-radius: 0.4rem;
    outline: none;
    transition: border-color 0.25s;

    &::placeholder {
      color: var(--muted2, #5c5a63);
    }

    &:focus {
      border-color: var(--red, #fe5f55);
    }
  }

  textarea {
    min-height: 12rem;
    resize: vertical;
  }
`;

const FormError = styled.div`
  font-size: 1.25rem;
  font-family: "Poppins-Medium";
  color: var(--red, #fe5f55);
  margin-bottom: 1.4rem;
`;

const FormOk = styled.div`
  font-size: 1.25rem;
  font-family: "Poppins-Medium";
  color: #7dd490;
  margin-bottom: 1.4rem;
`;

const SendBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 1.3rem;
  padding: 1.4rem 3rem;
  border-radius: 0.4rem;
  border: 1px solid
    ${(props) =>
      props.$disabled ? "var(--line2, rgba(255,255,255,0.14))" : "transparent"};
  cursor: ${(props) => (props.$disabled ? "wait" : "pointer")};
  background: ${(props) =>
    props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red, #fe5f55)"};
  color: ${(props) =>
    props.$disabled ? "var(--muted, #8e8c94)" : "var(--bg0, #0a0a0c)"};
  transition: background 0.3s var(--ease, ease);

  i {
    font-style: normal;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover {
    background: ${(props) =>
      props.$disabled ? "rgba(23, 23, 28, 0.85)" : "var(--red-deep, #e04a40)"};

    i {
      transform: translateX(0.4rem);
    }
  }
`;

const CtaRow = styled.div`
  display: flex;
  justify-content: center;
`;

const BtnRed = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  font-family: "Anton", sans-serif;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 1.3rem;
  padding: 1.5rem 3rem;
  border-radius: 0.4rem;
  border: none;
  cursor: pointer;
  background: var(--red, #fe5f55);
  color: var(--bg0, #0a0a0c);
  transition: all 0.3s var(--ease, ease);

  i {
    font-style: normal;
    transition: transform 0.3s var(--ease, ease);
  }

  &:hover {
    background: var(--red-deep, #e04a40);

    i {
      transform: translateX(0.4rem);
    }
  }
`;
